/**
 * Dialog per la gestione completa degli utenti
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import usersApiService, { User, Role, CreateUserDto, UpdateUserDto } from '../../services/usersApiService';

interface UserManagementDialogProps {
  open: boolean;
  user?: User | null;
  onClose: () => void;
  onSave: () => void;
}

const UserManagementDialog: React.FC<UserManagementDialogProps> = ({ open, user, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    roleId: '',
    isActive: true,
  });

  useEffect(() => {
    if (open) {
      loadRoles();
      if (user) {
        setFormData({
          username: user.username,
          email: user.email,
          password: '',
          confirmPassword: '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          roleId: user.roleId || '',
          isActive: user.isActive,
        });
      } else {
        setFormData({
          username: '',
          email: '',
          password: '',
          confirmPassword: '',
          firstName: '',
          lastName: '',
          roleId: '',
          isActive: true,
        });
      }
      setError(null);
    }
  }, [open, user]);

  const loadRoles = async () => {
    try {
      const rolesData = await usersApiService.getRoles();
      setRoles(rolesData.filter((r) => r.isActive));
    } catch (err: any) {
      console.error('Error loading roles:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });
  };

  const validateForm = (): string | null => {
    if (!formData.username.trim()) {
      return 'Lo username è obbligatorio';
    }
    if (!formData.email.trim()) {
      return 'L\'email è obbligatoria';
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      return 'L\'email non è valida';
    }
    if (!user && !formData.password) {
      return 'La password è obbligatoria per nuovi utenti';
    }
    if (formData.password && formData.password.length < 6) {
      return 'La password deve essere di almeno 6 caratteri';
    }
    if (formData.password && formData.password !== formData.confirmPassword) {
      return 'Le password non corrispondono';
    }
    if (!formData.roleId) {
      return 'Seleziona un ruolo per l\'utente';
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (user) {
        // Update existing user
        const updateData: UpdateUserDto = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          roleId: formData.roleId,
          isActive: formData.isActive,
        };
        await usersApiService.updateUser(user.id, updateData);

        // Update password if provided
        if (formData.password) {
          await usersApiService.changeUserPassword(user.id, formData.password);
        }
      } else {
        // Create new user
        const createData: CreateUserDto = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          roleId: formData.roleId,
        };
        await usersApiService.createUser(createData);
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nel salvataggio dell\'utente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{user ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            margin="normal"
            required
            disabled={!!user || loading}
            helperText={user ? 'Lo username non può essere modificato' : ''}
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            margin="normal"
            required
            disabled={loading}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Nome"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              margin="normal"
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Cognome"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              margin="normal"
              disabled={loading}
            />
          </Box>

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Ruolo</InputLabel>
            <Select
              name="roleId"
              value={formData.roleId}
              onChange={handleSelectChange}
              label="Ruolo"
              disabled={loading}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            {user ? 'Cambia Password (lascia vuoto per non modificare)' : 'Password'}
          </Typography>

          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            margin="normal"
            required={!user}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Conferma Password"
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            margin="normal"
            required={!user && !!formData.password}
            disabled={loading}
          />

          {user && (
            <FormControlLabel
              control={<Switch checked={formData.isActive} onChange={handleSwitchChange} name="isActive" />}
              label="Utente Attivo"
              sx={{ mt: 2 }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : user ? 'Aggiorna' : 'Crea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserManagementDialog;
